import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar, Nav, Offcanvas, Container, Button } from "react-bootstrap";
import InventoryIcon from "@mui/icons-material/Inventory";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LanguageIcon from "@mui/icons-material/Language";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import PeopleIcon from "@mui/icons-material/People";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";

import ProductsTable from "../ProductsTable";
import UsersTable from "../UsersTable";
import Variants from "../Variants";
import WebsitesTable from "../WebsitesTable";
import ScrapingProgressPage from "../../Pages/ScrapingProgress";
import { useUser } from "../../context/UserContext";
import { sidebarRoles } from "../../constants";

import "./style.css";

interface Props {
  user: any;
}

export default function Sidebar({ user }: Props) {
  const { setUser } = useUser();
  const [show, setShow] = useState(false);
  const [activeIndex, setActiveIndex] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedProductName, setSelectedProductName] = useState<string>("");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleLogOut = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("activeTab");
      localStorage.removeItem("selectedProductId");
      localStorage.removeItem("selectedProductName");
      setUser(null);
      setActiveIndex(null);
      setSelectedProductId(null);
      setSelectedProductName("");
      navigate("/login");
    } catch (error) {
      console.log(error);
    }
  };

  const handleNavItemClick = (item: string) => {
    const lowerItem = item.toLowerCase();
    setActiveIndex(lowerItem);
    localStorage.setItem("activeTab", lowerItem);
    setShow(false);
  };

  // 🔹 Restore active tab and selected product on reload
  useEffect(() => {
    const savedTab = localStorage.getItem("activeTab");
    if (savedTab) setActiveIndex(savedTab);
    else if (user) {
      const defaultTab =
        user.user_role === "admin"
          ? "users"
          : user.user_role === "editor"
          ? "websites"
          : "products";
      setActiveIndex(defaultTab);
      localStorage.setItem("activeTab", defaultTab);
    }

    const savedId = localStorage.getItem("selectedProductId");
    const savedName = localStorage.getItem("selectedProductName");
    if (savedId && savedName) {
      setSelectedProductId(Number(savedId));
      setSelectedProductName(savedName);
    }
  }, [user]);

  const allowedSidebarItems =
    sidebarRoles[user?.user_role || "viewer"] || ["Products"];

  const getIcon = (text: string, isActive: boolean) => {
    const iconProps = { fontSize: "medium" as const, className: "sidebar-icon" };
    switch (text) {
      case "Products":
        return isActive ? <InventoryIcon {...iconProps} /> : <Inventory2OutlinedIcon {...iconProps} />;
      case "Users":
        return isActive ? <PeopleIcon {...iconProps} /> : <PeopleOutlineOutlinedIcon {...iconProps} />;
      case "Websites":
        return isActive ? <LanguageIcon {...iconProps} /> : <LanguageOutlinedIcon {...iconProps} />;
      default:
        return null;
    }
  };

  const renderNavItems = () => (
    <>
      {user &&
        allowedSidebarItems.map((text) => (
          <Nav.Link
            key={text}
            className={`sidebar-nav-item ${
              activeIndex === text.toLowerCase() ? "active" : ""
            }`}
            onClick={() => handleNavItemClick(text)}
          >
            <div className="sidebar-nav-content">
              {getIcon(text, activeIndex === text.toLowerCase())}
              <span className="sidebar-nav-text">{text}</span>
            </div>
          </Nav.Link>
        ))}
    </>
  );

  return (
    <div className="sidebar-container">
      {/* Mobile Navigation */}
      <Navbar bg="white" className="d-lg-none mobile-navbar">
        <Container fluid>
          <Button variant="outline-secondary" onClick={handleShow} className="mobile-menu-btn">
            <MenuIcon fontSize="medium" />
          </Button>
        </Container>
      </Navbar>

      <Offcanvas show={show} onHide={handleClose} className="sidebar-offcanvas">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Navigation</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Nav className="flex-column sidebar-nav">{renderNavItems()}</Nav>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Desktop Sidebar */}
      <div className="sidebar-desktop d-none d-lg-block">
        <div className="sidebar-content">
          <Nav className="flex-column sidebar-nav">{renderNavItems()}</Nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <div className="auth-section">
          <div
            className="auth-button"
            onClick={token ? handleLogOut : () => navigate("/login")}
          >
            {token && (
              <>
                <LogoutIcon fontSize="small" />
                <span>Sign Out</span>
              </>
            )}
          </div>
        </div>

        <div className="content-area">
          {activeIndex === "scraping-progress" && (
            <ScrapingProgressPage
              onComplete={() => {
                setActiveIndex("products");
                localStorage.setItem("activeTab", "products");
              }}
            />
          )}
          {activeIndex === "users" && user?.user_role === "admin" && <UsersTable />}
          {activeIndex === "websites" && user?.user_role !== "viewer" && (
            <WebsitesTable
              onScrapingStart={() => {
                setActiveIndex("scraping-progress");
                localStorage.setItem("activeTab", "scraping-progress");
              }}
            />
          )}
          {activeIndex === "products" && (
            <ProductsTable
              onProductClick={(productId: number, productName: string) => {
                setSelectedProductId(productId);
                setSelectedProductName(productName);
                setActiveIndex("variants");
                localStorage.setItem("activeTab", "variants");
                localStorage.setItem("selectedProductId", productId.toString());
                localStorage.setItem("selectedProductName", productName);
              }}
            />
          )}
          {activeIndex === "variants" && selectedProductId && (
            <Variants
              productId={selectedProductId}
              productName={selectedProductName}
              handleBack={() => {
                setActiveIndex("products");
                localStorage.setItem("activeTab", "products");
                setSelectedProductId(null);
                setSelectedProductName("");
                localStorage.removeItem("selectedProductId"); 
                localStorage.removeItem("selectedProductName");
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
